import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loggerFunc } from 'utils/logger';
import useSafeAsync from 'hooks/useSafeAsync';
import productUpgradeEligibility from 'utils/productUpgradeEligibility.enum';
import policyUpgrade from 'utils/policyUpgrade.enum';
import * as SessionActions from 'actions/sessionActions';
import { GetProductUpgradeList } from 'services/MyRacService';
import { MY_COVER, RAC_LOYALTY_REWARDS, VEHICLE_DETAILS } from 'constants/routeConstants';
import Banner from 'components/Banner';
import VehiclesConfirmation from 'components/NextBestActionCard/VehiclesConfirmation';
import { ToastType } from 'components/Toast/Toast';
import PrizeIcon from 'assets/images/icons/prize-orange.svg';
import DefaultAlert from './DefaultAlert';
import PayfailAlert from './PayfailAlert';

const logger = loggerFunc('GridLayout');

const AlertsManager = ({
  racLoyaltyRewards,
  setToastMessage,
}) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const vehicleInfo = useSelector((state) => state.vehicles.vehicleInfo);
  const policyInfo = useSelector((state) => state.policy.policyInfo);
  const custInfo = useSelector((state) => state.customer.custInfo);
  const [loading, setLoading] = useState(true);
  const [reminderAlert, setReminderAlert] = useState(false);
  const [upgradesAlert, setUpgradesAlert] = useState(false);
  const safeAsync = useSafeAsync();

  // List of all Alert types sorted by priority
  const AlertTypes = {
    PAYFAIL: 'PAYFAIL',
    RAC_LOYALTY_REWARDS: 'RAC_LOYALTY_REWARDS',
    VEHICLE_EDIT: 'VEHICLE_EDIT',
    UPGRADES: 'UPGRADES',
    VEHICLE_REMINDERS: 'VEHICLE_REMINDERS',
    NONE: 'NONE',
  }

  // Alert priority
  let alertToShow = AlertTypes.NONE;
  if (!loading) {
    if (session?.showPayfailAlert) {
      alertToShow = AlertTypes.PAYFAIL;
    } else if (racLoyaltyRewards) {
      alertToShow = AlertTypes.RAC_LOYALTY_REWARDS;
    } else if (vehicleInfo.hasPolicyVehicleEditNextBestAction) {
      alertToShow = AlertTypes.VEHICLE_EDIT;
    } else if (upgradesAlert && !racLoyaltyRewards && !session?.racLoyaltyRewardShown) {
      alertToShow = AlertTypes.UPGRADES;
    } else if (reminderAlert && !racLoyaltyRewards && !session?.racLoyaltyRewardShown) {
      alertToShow = AlertTypes.VEHICLE_REMINDERS;
    }
  }

  const toggleAddReminderAlert = () => {
    // Hide alert using state
    setReminderAlert(!reminderAlert);
    // Store action in session so that alert is not displayed again until next user session
    dispatch(SessionActions.setReminderAlertShown(true));
  };

  const toggleUpgradesAlert = () => {
    // Hide alert using state
    setUpgradesAlert(!upgradesAlert);
    // Store action in session so that alert is not displayed again until next user session
    dispatch(SessionActions.setUpgradesAlertShown(true));
  };

  useEffect(() => {
    // Check session vehicle data to determine whether to display 'Add Reminders' alert
    if (vehicleInfo) {
      // eslint-disable-next-line prefer-const
      let reminderDates = [];
      const vehicles = vehicleInfo?.reminderVehicles;
      if (vehicles?.length > 0) {
        vehicles.forEach((vehicle) => {
          const { reminders } = vehicle;
          if (reminders?.length > 0) {
            reminders.forEach((reminder) => {
              if (!reminder?.date) {
                // Null date value means reminder has not been set
                reminderDates.push('Not set');
              }
            });
          }
        });
      }
      // Show alert if no vehicles found or reminderDates[] contains values where reminders 'Not set'
      if (!vehicles?.length || reminderDates?.length > 0) {
        setReminderAlert(true);
      }
    }
  }, [vehicleInfo]);

  useEffect(() => {
    if (!policyInfo || !custInfo) return;

    safeAsync(async () => {
      const upgradesData = await GetProductUpgradeList(custInfo.Id);

      if (upgradesData?.payload) {
        const upgrades = upgradesData.payload.filter((upgrade) => upgrade?.status !== productUpgradeEligibility.UpgradeInProgress) ?? [];

        if (upgrades.length > 0 && policyInfo?.AllowPolicyProductUpgrade !== policyUpgrade.NoOnlineUpgrade) {
          setUpgradesAlert(true);
        }
      }

      setLoading(false);
    }).catch(logger.error);
  }, [custInfo, policyInfo, safeAsync]);

  return (
    <>
      {alertToShow === AlertTypes.PAYFAIL && (
        <PayfailAlert />
      )}
      {alertToShow === AlertTypes.RAC_LOYALTY_REWARDS && (
        <Banner linkRef={RAC_LOYALTY_REWARDS} icon={PrizeIcon} title="Claim your FREE UPGRADE!" description="It's time to choose an upgrade - FREE for the next 12 months. <u>Claim now.</u><br /><span>T&C's apply.</span>" />
      )}
      {alertToShow === AlertTypes.VEHICLE_EDIT && (
        <VehiclesConfirmation
          onSuccessHandler={() => {
            setToastMessage({
              msg: 'Your vehicles were confirmed',
              type: ToastType.SUCCESS,
            });
          }}
          onErrorHandler={() => {
            setToastMessage({
              msg: 'Error occured while trying to confirm your vehicles',
              type: ToastType.ERROR,
            });
          }}
        />
      )}
      {alertToShow === AlertTypes.VEHICLE_REMINDERS && (
        <DefaultAlert
          toggleShow={toggleAddReminderAlert}
          heading="Never miss a key date again"
          message="You&apos;re missing a few vehicle reminders. Add them now so you don&apos;t miss them."
          eventTag="myrac.vehicleremindernotification"
          buttonText="Add reminders"
          linkRef={VEHICLE_DETAILS}
          isButton
        />
      )}
      {alertToShow === AlertTypes.UPGRADES && (
        <DefaultAlert
          toggleShow={toggleUpgradesAlert}
          heading="Looking for extra cover?"
          message="Avoid unexpected costs with our range of extra cover options."
          buttonText="View upgrades"
          linkRef={MY_COVER}
          isButton
        />
      )}
    </>
  )
};

export default AlertsManager;
